import React from "react";
import {IconDepartment, IconDocumentStaggeredLines, PersonChipBlack} from "../../../../../components/buttons/IconButtons";
import {CustomTextField} from "../../../../../components/CustomTextField";
import CustomAddChipSearch from "../../../../../components/CustomAddChipSearch";
import {USER_TYPE_MODEL} from "../../../../../enums/userTypeEnum";
import {getModelSimpleSearch, userListToLabelEmailPhoneValue} from "../../../../../Apis/functions";
import {TABLES} from "../../../../../Apis/Tables";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @description
 * Information every folio of material control opens with: the title and the
 * folio it is registered with, who requests it, who follows it and the
 * departments responsible for it.
 *
 * The fields each form captures on top of these travel as children, and the
 * prefix of the ids says which screen is capturing them.
 *
 * @param {Function} t - Translation function.
 * @param {string} idPrefix - Prefix of the ids of the fields.
 * @param {Object} materialRequest - Current form information.
 * @param {Function} handleChange - Change handler of the form.
 * @param {boolean} emptyFields - Indicates whether the required fields must be highlighted.
 * @param {Array} selectedEmployees - Employee that requests the folio.
 * @param {Function} setSelectedEmployees - Setter of the employee that requests the folio.
 * @param {Array} watchers - Users that follow the folio.
 * @param {Function} setWatchers - Setter of the users that follow the folio.
 * @param {Array} responsibleDepartments - Departments responsible for the folio.
 * @param {Function} setResponsibleDepartments - Setter of the responsible departments.
 * @param {React.ReactNode} children - Fields the form captures along with these.
 */
const RequestInformationSection = ({
    t,
    idPrefix,
    materialRequest,
    handleChange,
    emptyFields,
    selectedEmployees,
    setSelectedEmployees,
    watchers,
    setWatchers,
    responsibleDepartments,
    setResponsibleDepartments,
    children
}) => {

    return (
        <div className={'row border-radius-8px border-full-shades-300 mt-3'}>
            <div className={"d-flex p-2"}>
                <div className={"mr-2"}><IconDocumentStaggeredLines/></div>
                <p className={"fs-14px ibl-font-w-600"}>{t("request_information")}</p>
            </div>

            <div className={"border-bottom-shades-300 mb-3"}/>
            <div className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <CustomTextField
                    required={true}
                    id={`slt_${idPrefix}_title`}
                    name={'title'}
                    trans={'request_title'}
                    length={128}
                    value={materialRequest?.title}
                    onChange={handleChange}
                    emptyFields={emptyFields}
                />
            </div>
            <div className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <CustomTextField
                    required={true}
                    id={`slt_${idPrefix}_code`}
                    name={'code'}
                    trans={'folio'}
                    length={128}
                    value={materialRequest?.code}
                    onChange={handleChange}
                    emptyFields={emptyFields}
                />
            </div>
            <div className={"col-12"}>
                <CustomAddChipSearch
                    id={`slt_${idPrefix}_search_employees`}
                    selectedList={selectedEmployees}
                    setSelectedList={setSelectedEmployees}
                    userTypeIdsSearch={[
                        USER_TYPE_MODEL.EMPLOYEE
                    ]}
                    disabled={false}
                    label="requested_by"
                    required={false}
                    hideButton={true}
                    keyId={"userId"}
                    showSubSection={true}
                    sanitizeDataList={userListToLabelEmailPhoneValue}
                    Icon={PersonChipBlack}
                    helperText={t("requester_no_required")}
                />
            </div>

            <div className={"col-12"}>
                <CustomAddChipSearch
                    id={`slt_${idPrefix}_visible_to`}
                    selectedList={watchers}
                    setSelectedList={setWatchers}
                    userTypeIdsSearch={[
                        USER_TYPE_MODEL.EMPLOYEE
                    ]}
                    disabled={false}
                    label="visible_to_watchers"
                    hideButton={true}
                    keyId={"userId"}
                    showSubSection={true}
                    sanitizeDataList={userListToLabelEmailPhoneValue}
                    Icon={PersonChipBlack}
                    length={10}
                    helperText={t("these_users_can_view_this_request_and_will_be_notified_of_any_changes")}
                />
            </div>

            <div className={"col-12"}>
                <CustomAddChipSearch
                    id={`slt_${idPrefix}_responsible_departments`}
                    selectedList={responsibleDepartments}
                    setSelectedList={setResponsibleDepartments}
                    disabled={false}
                    label="responsible_departments"
                    required={true}
                    hideButton={true}
                    keyId={"id"}
                    showSubSection={false}
                    sanitizeDataList={userListToLabelEmailPhoneValue}
                    Icon={IconDepartment}
                    bodyApi={getModelSimpleSearch(TABLES.DEPARTMENT)}
                    emptyFields={emptyFields && responsibleDepartments.length === 0}
                    length={10}
                />
            </div>
            {children}
        </div>
    );
};

export default RequestInformationSection;
